import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router, RouterOutlet } from "@angular/router";
import { JobService } from "../../../../domains/jobs/services/job.service";
import { UiToastService } from "../../../../shared/components/toast/ui-toast.service";
import { Location } from "@angular/common";
import { SkeletonModule } from "primeng/skeleton";
import { LotService } from "../../../../domains/lots/services/lot.service";
import { AreaService } from "../../../../domains/areas/services/area.service";
import { JobIssueService } from "../../../../domains/jobIssues/services/job-issue-service";
import { MediaService } from "../../../../domains/media/services/media.service";
import { firstValueFrom } from "rxjs/internal/firstValueFrom";
import { AreaConfigurationService } from "../../../../domains/areas/services/area-configuration.service";
import { ZoneService } from "../../../../domains/zones/services/zone.service";
import { LoggerService } from "../../../../shared/services/logger.service";
import { AssertionIssuesService } from "../../../../domains/assertionIssues/services/assertion-issues.service";
import { AssertionService } from "../../../../domains/assertions/services/assertion.service";
import { ComponentService } from "../../../../domains/components/services/component.service";
import { BqInspectionDataService } from "../../../../domains/jobs/services/bq-inspection-data.service";
import { BqInspectionSingleAreaDataService } from "../../../../domains/jobs/services/bq-inspection-single-area-data.service";
import { FeatureFlag, FeatureService } from "../../../../core/feature-flags/feature.service";
import { JobIssueVendorService } from "../../../../domains/jobIssues/services/job-issue-vendor.service";
import { VendorService } from "../../../../domains/vendors/services/vendor-service";
import { ConstructionManagerScheduleService } from "../../../../domains/jobs/services/construction-manager-schedule.service";
import { BackgroundRefreshCoordinator } from "../../../../shared/utils/background-refresh-coordinator";
import { MissingDataCheckOptions, MissingDataService } from "../../../../shared/utils/missing-data-service";

@Component({
  selector: "app-bq4-root",
  standalone: true,
  imports: [RouterOutlet, SkeletonModule],
  providers: [
    BqInspectionSingleAreaDataService,
    // Children that inject the base type get this step's instance rather than a second, empty one.
    { provide: BqInspectionDataService, useExisting: BqInspectionSingleAreaDataService }
  ],
  templateUrl: "./bq4-root.component.html",
  styleUrl: "./bq4-root.component.scss"
})
export class Bq4RootComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private jobId = 0;

  constructor(
    private areaConfigurationService: AreaConfigurationService,
    private areasService: AreaService,
    private assertionIssuesService: AssertionIssuesService,
    private assertionsService: AssertionService,
    private componentsService: ComponentService,
    private jobIssueService: JobIssueService,
    private jobService: JobService,
    private location: Location,
    private logger: LoggerService,
    private lotService: LotService,
    private mediaService: MediaService,
    private route: ActivatedRoute,
    private toastService: UiToastService,
    private zoneService: ZoneService,
    private router: Router,
    protected bqInspectionDataService: BqInspectionSingleAreaDataService,
    private featureService: FeatureService,
    private vendorService: VendorService,
    private jobIssueVendorService: JobIssueVendorService,
    private constructionManagerScheduleService: ConstructionManagerScheduleService,
    private missingDataService: MissingDataService
  ) { }

  async ngOnInit(): Promise<void> {
    const isBq4Enabled = await firstValueFrom(this.featureService.isEnabled(FeatureFlag.EnableBQ234));
    if (!isBq4Enabled) {
      this.toastService.error("The BQ4 feature is not enabled for your account. Please contact support if you believe this is an error.");
      this.router.navigate(["/cm/schedule"]);
      return;
    }

    this.jobId = parseInt(this.route.snapshot.paramMap.get("jobId") ?? "0");
    const bqStep = this.route.snapshot.data["bqStep"] as string;

    // Subscribes to the CM schedule's background-refresh notification before the initial load runs, so
    // a refresh that lands mid-load is never silently missed, and re-reads local data on every later one.
    const coordinator = new BackgroundRefreshCoordinator(
      this.constructionManagerScheduleService.scheduleRefreshed,
      redirectOnMissingData => this.loadJobData(this.jobId, bqStep, redirectOnMissingData),
      this.destroyRef
    );
    this.bqInspectionDataService.isRefreshingInBackground = this.constructionManagerScheduleService.isFetchingSchedule;
    this.bqInspectionDataService.dataUpdatedPulse = coordinator.dataUpdatedPulse;

    await coordinator.runInitialLoad();
  }

  /**
   * Loads (or re-loads) all local data for the given BQ4 job. `redirectOnMissingData` should only be
   * true for the initial load — a background-refresh-triggered reload should never yank the user away
   * from a page they're already interacting with, so it just logs and no-ops instead.
   */
  private async loadJobData(jobId: number, bqStep: string, redirectOnMissingData: boolean): Promise<boolean> {
    const bq4Job = await this.jobService.getJobById(jobId);
    const missingDataOptions = this.buildMissingDataOptions(jobId, redirectOnMissingData, bq4Job?.lotId);

    if (this.missingDataService.isMissingData(bq4Job, missingDataOptions.job)) {
      return false;
    }

    if (this.missingDataService.isMissingData(bqStep !== `BQ${bq4Job.buildQualityStep}` ? null : bqStep, missingDataOptions.bqStepMismatch)) {
      return false;
    }

    const lot = await this.lotService.getLotById(bq4Job.lotId);
    if (this.missingDataService.isMissingData(lot, missingDataOptions.lot)) {
      return false;
    }

    const zones = await this.zoneService.getZonesByLotId(bq4Job.lotId);
    if (this.missingDataService.isMissingData(zones, missingDataOptions.zones)) {
      return false;
    }

    const startTime = performance.now();

    const [areas, jobIssues, assertionIssues, components, assertions] = await Promise.all([
      this.areasService.getAreasByLotId(bq4Job.lotId),
      this.jobIssueService.getJobIssuesForJob(bq4Job.id),
      this.assertionIssuesService.getAssertionIssuesForJob(bq4Job.id),
      this.componentsService.getComponentsForJob(bq4Job.id),
      this.assertionsService.getAssertionsForJob(bq4Job.id),
    ]);

    const areasByBuildQualityStep = areas.filter(area => area.buildQualityStepScope === bq4Job.buildQualityStep);
    const availableRooms = areas.filter(area => !!area.buildQualityStepScope === false);
    const zonesForAreas = zones.filter(zone => areasByBuildQualityStep.some(area => area.zoneId === zone.id));

    const [issueMedia, assertionMedia] = await Promise.all([
      this.mediaService.getMediaForJobIssues([...jobIssues, ...assertionIssues]),
      this.mediaService.getMediaForAssertions(assertions),
    ]);

    const allIssueIdentifiers = [...new Set([...jobIssues, ...assertionIssues].map(issue => issue.identifier))];
    const jobIssueVendors = await this.jobIssueVendorService.getAllActiveVendorsForIssues(allIssueIdentifiers);

    const endTime = performance.now();
    this.logger.info(`[Bq4RootComponent] Time taken to fetch job details from indexedDB: ${endTime - startTime} ms.`);

    firstValueFrom(this.areaConfigurationService.getConfiguration(bq4Job.divisionId))
      .then(areasConfig => {
        this.bqInspectionDataService.areasConfig.set(areasConfig);
      })
      .catch(error => this.logger.error("[Bq4RootComponent] Failed to load area configuration", error));

    this.vendorService.getVendorsForLotId(lot.id).then(vendorData => {
      const vendors = this.vendorService.processVendorData(vendorData);
      this.bqInspectionDataService.vendors.set(vendors);
    })
      .catch(error => this.logger.error("[Bq4RootComponent] Failed to load vendors for lot", error));

    this.bqInspectionDataService.job.set(bq4Job);
    this.bqInspectionDataService.lot.set(lot);
    this.bqInspectionDataService.areas.set(areasByBuildQualityStep);
    this.bqInspectionDataService.availableRooms.set(availableRooms);
    this.bqInspectionDataService.jobIssues.set(jobIssues);
    this.bqInspectionDataService.media.set([...issueMedia, ...assertionMedia]);
    this.bqInspectionDataService.zones.set(zonesForAreas);
    this.bqInspectionDataService.lotZones.set(zones);
    this.bqInspectionDataService.assertionIssues.set(assertionIssues);
    this.bqInspectionDataService.jobIssueVendors.set(jobIssueVendors);
    this.bqInspectionDataService.components.set(components);
    this.bqInspectionDataService.assertions.set(assertions);
    this.bqInspectionDataService.isCcmInspection.set(false);
    return true;
  }

  private buildMissingDataOptions(jobId: number, redirectOnMissingData: boolean, lotId?: number): Record<"job" | "bqStepMismatch" | "lot" | "zones", MissingDataCheckOptions> {
    return {
      job: {
        redirectOnMissingData,
        redirect: { type: "navigate", route: ["/cm/schedule"] },
        errorMessage: "Error loading job data for BQ4. Please try again.",
        logMessage: `Background refresh could not find job ${jobId}`,
        componentTag: "Bq4RootComponent"
      },
      bqStepMismatch: {
        redirectOnMissingData,
        redirect: { type: "navigate", route: ["/cm/schedule"] },
        errorMessage: "The job's build quality step does not match the expected step. Please try again.",
        logMessage: `Background refresh found a mismatched build quality step for job ${jobId}`,
        componentTag: "Bq4RootComponent"
      },
      lot: {
        redirectOnMissingData,
        redirect: { type: "back" },
        errorMessage: "Error loading related lot data for BQ4. Please try again.",
        logMessage: `Background refresh could not find lot ${lotId}`,
        componentTag: "Bq4RootComponent"
      },
      zones: {
        redirectOnMissingData,
        redirect: { type: "back" },
        errorMessage: "Error loading related zone data for BQ4. Please try again.",
        logMessage: `Background refresh could not find zones for lot ${lotId}`,
        componentTag: "Bq4RootComponent"
      }
    };
  }
}
